import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { and, notEq } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsLinkInline,
  HdsButton,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Pagination,
  FlexGrid,
  Flex,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import ListTable from 'hcp/components/iam/groups/list-table';
import Create from 'hcp/components/iam/groups/stepper/create';

/**
 * `PageGroupsOrganizationList` is the parent wrapper component for the HCP Groups Organization list page.
 *
 * @argument groups {Object}
 *
 * Usage:
 * ```
 * <Page::Groups::Organization::List
 *  @groups={{this.model.groups}}
 * />
 * ```
 *
 * @class PageGroupsOrganizationList
 */
export default class PageGroupsOrganizationList extends Component {
  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |P|>
          <P.Title>
            {{t "components.page.access-control.groups.list.title"}}
          </P.Title>
          <P.Breadcrumb>
            <BreadcrumbsContainer />
          </P.Breadcrumb>
          <P.IconTile @icon="users" />
          <P.Description>
            {{t "components.page.access-control.groups.list.description"}}
            <HdsLinkInline
              @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/groups"
              @icon="docs-link"
              @isHrefExternal={{true}}
              data-test-description-external-link
            >
              {{t "components.page.access-control.groups.list.cta"}}
            </HdsLinkInline>
          </P.Description>
          <P.Actions>
            {{#if (and (can "delete groups") (notEq @groups.length 0))}}
              <HdsButton
                @text={{t
                  "components.page.access-control.groups.create.action"
                }}
                @icon="plus"
                @iconPosition="leading"
                @color="primary"
                @route="cloud.access-control.groups.create"
              />
            {{/if}}
          </P.Actions>
        </HdsPageHeader>
      </:header>
      {{! TODO: https://hashicorp.atlassian.net/browse/HCPIE-430 }}
      {{! <:alert>
    <Hds::Alert @type="inline" as |A|>
      <A.Title>
        Title here
      </A.Title>
      <A.Description>
        Description here
      </A.Description>
    </Hds::Alert>
  </:alert> }}
      <:body>
        {{#if @groups.length}}
          <section data-test-groups-organization-list-table>
            <ListTable
              @groups={{@groups}}
              @groupsPermissions={{@groupsPermissions}}
              @scimEnabled={{@scimEnabled}}
              @ssoEnabled={{@ssoEnabled}}
            />
            <Pagination @pagination={{@pagination}} />
          </section>
        {{else}}
          {{#if (can "create groups")}}
            {{! zero state stepper}}
            <FlexGrid as |G|>
              <G.Item @xs="12" @sm="12" @md="12" @lg="6" @xl="6">
                <Flex @direction="column" @gap="lg" {{boxMargin "0 0 lg 0"}}>
                  <Create />
                </Flex>
              </G.Item>
            </FlexGrid>
          {{else}}
            <div {{boxPadding "2xl 0 0 0"}}>
              <HdsApplicationState as |A|>
                <A.Header
                  @title={{t "helpers.options-for-http-error.403.label"}}
                  data-test-page-groups-project-list-zero-header
                />
                <A.Body
                  @text={{t "helpers.options-for-http-error.403.message"}}
                  data-test-page-groups-project-list-zero-body
                />
              </HdsApplicationState>
            </div>
          {{/if}}
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
